/**
 * Canonical URL Helper Functions
 * Handles canonical URL generation for SEO according to Google Search Console requirements
 */

import { urlMappings } from './hreflang';

const BASE_URL = 'https://gpt-watermark-remover.com';

/**
 * Remove all query parameters from a URL
 * @param url - Full URL or pathname
 * @returns URL without query parameters
 */
export function stripQueryParams(url: string): string {
  // Handle both full URLs and pathnames
  if (url.includes('?')) {
    return url.split('?')[0];
  }
  return url;
}

/**
 * Normalize trailing slashes according to canonical rules
 * @param path - URL pathname
 * @returns Normalized path
 */
export function normalizeTrailingSlash(path: string): string {
  // Root path always stays as '/'
  if (path === '/') {
    return '/';
  }

  // Remove trailing slash for all other paths (consistency for all locales)
  // All locale homepages (/de, /pt-br, /my) should have no trailing slash for consistency
  if (path.endsWith('/') && path.length > 1) {
    return path.slice(0, -1);
  }

  return path;
}

/**
 * Detect locale from pathname
 * @param pathname - URL pathname
 * @returns Detected locale or 'en' as default
 */
export function getLocaleFromPath(pathname: string): 'en' | 'de' | 'pt-BR' | 'ms-MY' | 'es' | 'fr' | 'ja' | 'id' {
  // Must match exact locale prefix followed by / or end of string
  // Critical: /detect should NOT match /de because it's not a German locale path
  if (pathname === '/my' || pathname.startsWith('/my/')) return 'ms-MY';
  if (pathname === '/pt-br' || pathname.startsWith('/pt-br/')) return 'pt-BR';
  if (pathname === '/es' || pathname.startsWith('/es/')) return 'es';
  if (pathname === '/de' || pathname.startsWith('/de/')) return 'de';
  if (pathname === '/fr' || pathname.startsWith('/fr/')) return 'fr';
  if (pathname === '/ja' || pathname.startsWith('/ja/')) return 'ja';
  if (pathname === '/id' || pathname.startsWith('/id/')) return 'id';
  return 'en';
}

/**
 * Remove locale prefix from pathname and translate back to English canonical slug
 * Handles:
 * - Regular pages with urlMappings (e.g., /de/ueber-uns → /about)
 * - Blog posts with slug translations (e.g., /de/blog/slug → /blog/english-slug)
 * - Knowledge articles with slug translations (e.g., /de/wissen/slug → /knowledge/english-slug)
 *
 * @param pathname - URL pathname (can be translated slug like "/de/ueber-uns" or "/de/blog/localized-slug")
 * @returns Canonical English pathname without locale prefix (e.g., "/about" or "/blog/english-slug")
 */
export function removeLocalePrefix(pathname: string): string {
  // First, detect the locale and strip the prefix
  const locale = getLocaleFromPath(pathname);
  const pathWithoutLocale = pathname.replace(/^\/(en|de|pt-br|my|es|fr|ja|id)(\/|$)/, '$2') || '/';

  // Handle blog posts with slug translation
  // Pattern: /blog/localized-slug → /blog/english-slug
  const blogMatch = pathWithoutLocale.match(/^\/blog\/([^/]+)$/);
  if (blogMatch) {
    const localizedSlug = blogMatch[1];
    try {
      // Import slug mappings synchronously for reverse lookup
      const blogSlugMappings = {
        'de': require('../blog/slugMappings/de.json'),
        'pt-BR': require('../blog/slugMappings/pt-BR.json'),
        'ms-MY': require('../blog/slugMappings/ms-MY.json'),
        'es': require('../blog/slugMappings/es.json'),
        'en': require('../blog/slugMappings/en.json'),
        'fr': require('../blog/slugMappings/fr.json'),
        'ja': require('../blog/slugMappings/ja.json'),
        'id': require('../blog/slugMappings/id.json'),
      };

      const mapping = blogSlugMappings[locale as keyof typeof blogSlugMappings];
      if (!mapping) return pathWithoutLocale;

      // Reverse lookup: find English slug from localized slug
      for (const [englishSlug, localSlug] of Object.entries(mapping)) {
        if (localSlug === localizedSlug) {
          return `/blog/${englishSlug}`;
        }
      }

      // If not found in mapping, assume it's already English
      return pathWithoutLocale;
    } catch (e) {
      // Fallback if import fails
      return pathWithoutLocale;
    }
  }

  // Handle blog category pages with localized paths and slugs
  // Pattern: /blog/kategorie/localized-slug OR /blog/categoria/slug OR /blog/kategori/slug → /blog/category/english-slug
  const categoryMatch = pathWithoutLocale.match(/^\/blog\/(category|kategorie|categoria|kategori)\/([^/]+)$/);
  if (categoryMatch) {
    const categorySlug = categoryMatch[2];
    try {
      // Import category slug mappings for reverse lookup
      const { getEnglishCategoryId } = require('../blog/categorySlugMapping');
      const englishId = getEnglishCategoryId(categorySlug, locale);
      return `/blog/category/${englishId}`;
    } catch (e) {
      // Fallback if import fails
      return `/blog/category/${categorySlug}`;
    }
  }

  // Handle knowledge articles with slug translation
  // Pattern: /wissen/localized-slug or /conhecimento/slug or /pengetahuan/slug or /conocimiento/slug → /knowledge/english-slug
  const knowledgeBasePaths: Record<string, string> = {
    'wissen': 'de',
    'conhecimento': 'pt-BR',
    'pengetahuan': 'ms-MY',
    'conocimiento': 'es',
    'knowledge': 'en',
    'connaissances': 'fr',
    'chishiki': 'ja',
    'pengetahuan-id': 'id',
  };

  for (const [basePath, pathLocale] of Object.entries(knowledgeBasePaths)) {
    const knowledgeMatch = pathWithoutLocale.match(new RegExp(`^\\/${basePath}\\/([^/]+)$`));
    if (knowledgeMatch) {
      const localizedSlug = knowledgeMatch[1];
      try {
        const { getEnglishKnowledgeSlug } = require('../knowledge/slugMapping');
        const englishSlug = getEnglishKnowledgeSlug(localizedSlug, locale);
        return `/knowledge/${englishSlug}`;
      } catch (e) {
        // Fallback if import fails
        return `/knowledge/${localizedSlug}`;
      }
    }
  }

  // Check if this is a translated page slug and convert back to canonical English
  for (const [canonicalPath, translations] of Object.entries(urlMappings)) {
    for (const translation of Object.values(translations)) {
      if (translation === pathWithoutLocale) {
        return canonicalPath;
      }
    }
  }

  // If no mapping found, return the path as-is (already without locale)
  return pathWithoutLocale;
}

/**
 * Check if a path is a locale homepage
 * Handles both with and without trailing slashes: /de, /de/, /pt-br, /pt-br/, etc.
 *
 * NOTE: Locale homepages should use SELF-REFERENCING canonicals (not canonicalize to /)
 * Google recommends self-referencing canonicals for i18n pages, using hreflang to show relationships.
 * See: https://developers.google.com/search/docs/specialty/international/localized-versions
 */
function isLocaleHomepage(pathname: string): boolean {
  // Normalize: remove trailing slash for comparison
  const normalized = pathname.endsWith('/') && pathname.length > 1
    ? pathname.slice(0, -1)
    : pathname;
  const localeHomepages = ['/de', '/pt-br', '/my', '/es', '/en', '/fr', '/ja', '/id'];
  return localeHomepages.includes(normalized);
}

/**
 * Get canonical URL for any page
 *
 * Canonical Rules (Google i18n Best Practices):
 * 1. ALL pages use self-referencing canonical URLs
 *    - / → https://gpt-watermark-remover.com/
 *    - /de → https://gpt-watermark-remover.com/de
 *    - /pt-br → https://gpt-watermark-remover.com/pt-br
 *    - /de/ueber-uns → https://gpt-watermark-remover.com/de/ueber-uns
 * 2. Use hreflang tags to indicate language relationships (handled separately)
 * 3. Strip query parameters from ALL URLs
 *
 * IMPORTANT: For internationalized sites, Google recommends:
 * - Self-referencing canonicals for each language version
 * - Hreflang tags to show relationships between translations
 * - NOT canonicalizing translations to the "original" language
 *
 * See: https://developers.google.com/search/docs/specialty/international/localized-versions
 *
 * @param pathname - Current URL pathname (e.g., '/de', '/de/ueber-uns', '/blog/my-post?ref=twitter')
 * @returns Absolute canonical URL
 */
export function getCanonicalUrl(pathname: string): string {
  // Step 1: Remove query parameters
  let cleanPath = stripQueryParams(pathname);

  // Step 2: Normalize trailing slashes
  cleanPath = normalizeTrailingSlash(cleanPath);

  // Step 3: Return self-referencing canonical URL for ALL pages
  // Locale homepages (/de, /pt-br, /my, /es) get their own canonical
  // Hreflang tags handle the relationship between language versions
  return `${BASE_URL}${cleanPath}`;
}

/**
 * Get hreflang URLs for a given pathname
 * Returns all language variants for the current page
 *
 * @param pathname - Current URL pathname
 * @returns Object with hreflang URLs for all locales
 */
export function getHreflangUrls(pathname: string): {
  en: string;
  de: string;
  'pt-BR': string;
  'ms-MY': string;
  es: string;
  fr: string;
  ja: string;
  id: string;
  'x-default': string;
} {
  // Remove query params and locale prefix
  const cleanPath = stripQueryParams(pathname);
  const pathWithoutLocale = removeLocalePrefix(cleanPath);

  // Build language variants
  // Ensure all paths start with /
  // Consistent: root / keeps slash, all locale prefixes have no trailing slash
  const enPath = pathWithoutLocale; // Already starts with / from removeLocalePrefix
  const dePath = pathWithoutLocale === '/' ? '/de' : `/de${pathWithoutLocale}`;
  const brPath = pathWithoutLocale === '/' ? '/pt-br' : `/pt-br${pathWithoutLocale}`;
  const myPath = pathWithoutLocale === '/' ? '/my' : `/my${pathWithoutLocale}`;
  const esPath = pathWithoutLocale === '/' ? '/es' : `/es${pathWithoutLocale}`;
  const frPath = pathWithoutLocale === '/' ? '/fr' : `/fr${pathWithoutLocale}`;
  const jaPath = pathWithoutLocale === '/' ? '/ja' : `/ja${pathWithoutLocale}`;
  const idPath = pathWithoutLocale === '/' ? '/id' : `/id${pathWithoutLocale}`;

  return {
    'en': `${BASE_URL}${enPath}`,
    'de': `${BASE_URL}${dePath}`,
    'pt-BR': `${BASE_URL}${brPath}`,
    'ms-MY': `${BASE_URL}${myPath}`,
    'es': `${BASE_URL}${esPath}`,
    'fr': `${BASE_URL}${frPath}`,
    'ja': `${BASE_URL}${jaPath}`,
    'id': `${BASE_URL}${idPath}`,
    'x-default': `${BASE_URL}${enPath}`, // x-default points to English
  };
}

/**
 * Check if current URL is a search page
 * Search pages should have noindex, nofollow
 *
 * @param url - Full URL or pathname
 * @returns True if this is a search page
 */
export function isSearchPage(url: string): boolean {
  return url.includes('?q=');
}

/**
 * Get robots meta content for a page
 * @param pathname - URL pathname
 * @returns Robots directive string or null
 */
export function getRobotsDirective(pathname: string): string | null {
  // Search pages should not be indexed
  if (isSearchPage(pathname)) {
    return 'noindex, nofollow';
  }

  const cleanPath = stripQueryParams(pathname);
  const pathWithoutLocale = removeLocalePrefix(cleanPath);

  // Tag pages should not be indexed (thin content risk)
  // Tags are excluded from sitemap to avoid thin content penalties
  if (pathWithoutLocale.includes('/blog/tag/')) {
    return 'noindex, follow';
  }

  // Pricing, comparison, imprint, feature-request should be noindex
  const noindexPaths = [
    '/pricing',
    '/comparison',
    '/imprint',
    '/feature-request',
  ];

  if (noindexPaths.includes(pathWithoutLocale)) {
    return 'noindex, follow';
  }

  // Default: allow indexing
  return null;
}
